import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LEDGER_KIND_LABELS, type LedgerKind } from '@shared/domain';
import { api, actionKey } from '../../api';
import { useSession } from '../../session';
import type { OpenItem, RelSummary } from '../../types';
import { Badge, ConfirmDialog, Empty, ErrorBox, Field, Loading, RupeeInput, dt, inr, rupeesToPaise, statusTone, useBusy } from '../../ui';

interface Adjustment {
  _id: string;
  number: string;
  relationshipId: string;
  direction: 'CREDIT' | 'DEBIT';
  amountPaise: string;
  reason: string;
  status: string;
  proposedBy: { name: string };
  proposedAt: string;
  buyerDecisionNote?: string;
}

export function Adjustments() {
  const { can } = useSession();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const q = useQuery({ queryKey: ['adjustments'], queryFn: () => api.get<Adjustment[]>('/api/adjustments') });
  const [decide, setDecide] = useState<{ a: Adjustment; d: 'APPROVE' | 'REJECT' } | null>(null);
  const [note, setNote] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <div className="stack">
      {can('supplier.adjustments.propose') ? <ProposeForm initialRel={params.get('relationshipId') ?? ''} disputeId={params.get('disputeId') ?? undefined} onDone={() => void qc.invalidateQueries()} /> : null}
      <div className="card">
        <h1>Corrections</h1>
        <p className="muted">A reduction (credit note) lowers what the company owes and is posted at once. An extra charge needs the company’s approval first. The original supply entry is never changed.</p>
        {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
        {q.data && !q.data.length ? <Empty>No corrections.</Empty> : null}
        <ul className="list">
          {q.data?.map((a) => (
            <li key={a._id} className="row between">
              <div>
                <strong>{a.direction === 'CREDIT' ? 'Reduction' : 'Extra charge'} {inr(a.amountPaise)}</strong> · {a.reason}
                <div><small>{a.number} · {a.proposedBy.name} · {dt(a.proposedAt)}{a.buyerDecisionNote ? ` · company: ${a.buyerDecisionNote}` : ''}</small></div>
              </div>
              <div className="row">
                <Badge tone={statusTone(a.status)}>{a.status.replace(/_/g, ' ').toLowerCase()}</Badge>
                {can('buyer.adjustments.approve') && a.status === 'PENDING_BUYER_APPROVAL' ? <><button className="btn small primary" onClick={() => setDecide({ a, d: 'APPROVE' })}>Approve</button><button className="btn small" onClick={() => setDecide({ a, d: 'REJECT' })}>Reject</button></> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
      {decide ? (
        <ConfirmDialog open title={decide.d === 'APPROVE' ? 'Approve this correction?' : 'Reject this correction?'} lines={[['Type', decide.a.direction === 'CREDIT' ? 'Reduction' : 'Extra charge'], ['Amount', inr(decide.a.amountPaise)], ['Reason', decide.a.reason]]} confirmLabel={decide.d === 'APPROVE' ? 'Approve' : 'Reject'} danger={decide.d === 'REJECT'} busy={busy} error={error} onClose={() => setDecide(null)} onConfirm={() => void run(async () => { const k = actionKey(`adj-decide:${decide.a._id}`); await api.post(`/api/adjustments/${decide.a._id}/decide`, { decision: decide.d, note }, { idempotencyKey: k.key }); k.done(); setDecide(null); await qc.invalidateQueries(); })}>
          <Field label="Note"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function ProposeForm({ initialRel, disputeId, onDone }: { initialRel: string; disputeId?: string; onDone: () => void }) {
  const dash = useQuery({ queryKey: ['supplier-dash'], queryFn: () => api.get<{ customers: RelSummary[] }>('/api/supplier/dashboard') });
  const [rel, setRel] = useState(initialRel);
  const ledger = useQuery({ queryKey: ['ledger', rel], queryFn: () => api.get<{ openItems: OpenItem[] }>(`/api/relationships/${rel}/ledger`), enabled: Boolean(rel) });
  const [dir, setDir] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [item, setItem] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);
  const { busy, error, run } = useBusy();
  return (
    <div className="card stack">
      <h2>Propose a correction</h2>
      <Field label="Customer"><select value={rel} onChange={(e) => setRel(e.target.value)}><option value="">Choose…</option>{dash.data?.customers.map((c) => <option key={c.relationshipId} value={c.relationshipId}>{c.counterpartyName}</option>)}</select></Field>
      <div className="row" role="radiogroup">
        <label className="check"><input type="radio" checked={dir === 'CREDIT'} onChange={() => setDir('CREDIT')} /> Reduce what they owe</label>
        <label className="check"><input type="radio" checked={dir === 'DEBIT'} onChange={() => setDir('DEBIT')} /> Add an extra charge (needs their approval)</label>
      </div>
      {dir === 'CREDIT' ? (
        <Field label="Which bill?">
          <select value={item} onChange={(e) => setItem(e.target.value)}>
            <option value="">Choose…</option>
            {ledger.data?.openItems.map((i) => <option key={i.itemId} value={i.itemId}>{LEDGER_KIND_LABELS[i.kind as LedgerKind]} · {i.description.slice(0, 50)} · due {inr(i.unpaidPaise)}</option>)}
          </select>
        </Field>
      ) : null}
      <Field label="Amount (₹)"><RupeeInput value={amount} onChange={setAmount} /></Field>
      <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <button className="btn primary" disabled={!rel || !rupeesToPaise(amount) || !reason || (dir === 'CREDIT' && !item)} onClick={() => setOpen(true)}>Continue</button>
      <ConfirmDialog open={open} title="Save this correction?" lines={[['Type', dir === 'CREDIT' ? 'Reduction (posted now)' : 'Extra charge (waits for company approval)'], ['Amount', inr(rupeesToPaise(amount))], ['Reason', reason]]} confirmLabel="Save" busy={busy} error={error} onClose={() => setOpen(false)} onConfirm={() => void run(async () => {
        const k = actionKey(`adj:${rel}:${item}:${amount}`);
        await api.post('/api/adjustments', { relationshipId: rel, direction: dir, amountPaise: rupeesToPaise(amount), targetItemId: dir === 'CREDIT' ? item : undefined, reason, disputeId }, { idempotencyKey: k.key });
        k.done();
        setOpen(false);
        setAmount('');
        setReason('');
        onDone();
      })} />
    </div>
  );
}
